/*
 * frankly-skin-test.js — Frank!y sampling funnel.
 * Vanilla JS, geen dependencies. Eén bestand bedient twee pagina's: de huidtest-modal
 * en de claimpagina. Ontbreekt de bijbehorende markup, dan no-opt het script netjes.
 * DOM-contract: CONTRACT.md §4 — wijk daar niet van af.
 *
 * TWEE SOORTEN MAIL, STRENG GESCHEIDEN
 *
 *   TRANSACTIONEEL — de bevestigingsmail met de claimlink. Iemand vraagt een sample
 *   aan, wij mailen waar dat heen mag. Dat is de afhandeling van zijn eigen verzoek,
 *   geen reclame, dus daar is géén marketingtoestemming voor nodig. Deze mail gaat
 *   dus ALTIJD weg, vinkje of niet — voor zover het gekozen kanaal het kan.
 *
 *   MARKETING — de nieuwsbrief (15% korting op de eerste aankoop vanaf € 20). Vereist
 *   toestemming, dus gaat alleen weg met het vinkje aan. Het vinkje begint leeg en
 *   wordt hier nooit programmatisch aangezet: een vooraf aangevinkt vakje is geen
 *   geldige toestemming (AVG overweging 32; HvJ Planet49, C-673/17).
 *
 * Wat welk kanaal kan:
 *
 *   esp 'klaviyo'  — transactioneel via het client EVENTS-endpoint (alleen company id
 *                    nodig; maakt/werkt het profiel bij zónder inschrijving).
 *                    Marketing via het client SUBSCRIPTIONS-endpoint (list id erbij).
 *   esp 'shopify'  — alléén marketing: {% form 'customer' %} ís een inschrijving.
 *                    Er bestaat hier geen transactionele route. De enige mail die deze
 *                    route oplevert is de opt-in-bevestiging van Shopify, en die
 *                    bestaat alleen als dubbele opt-in aan staat (Instellingen →
 *                    Klanten). Dat kan het theme niet uitlezen; de winkelier bevestigt
 *                    het met de configsleutel shopifyDoubleOptIn.
 *   esp 'none'     — niets.
 *
 * Daaruit volgt canEmail(): kan er voor deze bezoeker echt een mail uitgaan? Zo nee,
 * dan slaan we het inbox-scherm ("kijk in je mail") over en sturen we direct door naar
 * de claimpagina. Dat scherm mag nooit post beloven die niet komt.
 *
 * canEmail() kijkt daarom naar drie dingen, niet alleen naar de configuratie:
 * kán het kanaal mailen, mócht er gemaild worden (vinkje waar dat telt), en is de
 * POST die de mail veroorzaakt ook echt aangekomen (mailFailed). Dat laatste is geen
 * theorie: a.klaviyo.com staat op de gangbare tracker-blocklists, dus voor een deel
 * van de bezoekers wordt dat verzoek structureel geweigerd.
 *
 * Alle verzending is fire-and-forget: fouten landen in console.warn, de flow wacht
 * nergens op en de sample hangt van geen enkel verzoek af.
 */
(function () {
  'use strict';

  // ---------- config ----------

  var DEFAULTS = {
    storageKey: 'frankly.sample.v1',
    claimUrl: '/pages/sample-claim',
    esp: 'shopify',
    klaviyoCompanyId: '',
    klaviyoListId: '',
    // Naam van het transactionele Klaviyo-event. Hierop hangt de flow met de
    // claimlink; wijzig je hem hier of in het configblok, wijzig dan ook de trigger
    // van die flow, anders komt er stilzwijgend geen mail meer aan.
    klaviyoMetric: 'Sample aangevraagd',
    // Alleen van belang bij esp 'shopify'. Stuurt Shopify zelf een bevestigingsmail?
    // Dat hangt aan "dubbele opt-in" onder Instellingen → Klanten — een instelling
    // buiten het theme, die Liquid noch JS kan uitlezen. Staat hij uit, dan wordt de
    // klant stilzwijgend ingeschreven en gaat er HELEMAAL geen mail uit; het
    // inbox-scherm zou dan post beloven die nooit komt. Daarom bevestigt de winkelier
    // dit zelf in de sectie-instelling, en gaan we er zonder die bevestiging van uit
    // dat er geen mail is: bij twijfel beloven we er geen.
    shopifyDoubleOptIn: false,
    tieOrder: ['oily', 'combi', 'blemish'],
    timeLines: {
      'Onder een minuut': 'Onder een minuut per dag. Dat lukt met deze twee.',
      'Een paar minuten': 'Twee producten, twee minuten per dag.',
      'Zoveel als nodig': 'Je neemt er de tijd voor. Deze twee vragen er weinig.'
    },
    timeLineDefault: 'Eén sample per persoon. Twee dagen uitproberen.',
    emailError: 'Daar komt geen mail aan. Check het adres.',
    customerTagPrefix: 'huidtype:',
    funnelTag: 'sample-funnel'
  };

  // Vaste NL-teksten die niet uit het configblok komen.
  var TXT_CLEAR = 'Je winkelwagen wordt geleegd zodat het sample gratis verzonden wordt. Nog een keer klikken om door te gaan.';
  var TXT_FAILED = 'Toevoegen lukte niet. Probeer het opnieuw of mail ons.';
  var TXT_CLEAR_FAILED = 'Je winkelwagen legen lukte niet. Probeer het opnieuw.';
  var TXT_CONFIRM = 'Ja, leeg mijn winkelwagen';
  var TXT_NO_VARIANT = 'Dit sample is nog niet gekoppeld in de admin. Neem contact met ons op, dan sturen we het alsnog op.';

  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
  var TITLE_ID = 'frankly-sheet-title';
  var STEPS = ['q1', 'q2', 'q3', 'q4', 'q5', 'q6', 'email', 'result', 'inbox'];
  var MAX_AGE = 7 * 24 * 60 * 60 * 1000; // 7 dagen
  var KLAVIYO_REVISION = '2026-07-15';

  // Configblok lezen; ontbreekt of breekt het, dan gewoon de defaults.
  //
  // Het configblok bevat ook "newsLabel", de copy naast het nieuwsbrief-vinkje. Die
  // sleutel staat hier bewust NIET in DEFAULTS: het label wordt server-side gerenderd,
  // JS hoeft de tekst niet te kennen en mag hem al helemaal niet dupliceren. Eén plek
  // voor die copy, en dat is het configblok in de sectie.
  var cfg = (function () {
    var out = {}, k;
    for (k in DEFAULTS) if (DEFAULTS.hasOwnProperty(k)) out[k] = DEFAULTS[k];
    var node = document.querySelector('script[type="application/json"][data-frankly-config]');
    if (node) {
      try {
        var raw = JSON.parse(node.textContent);
        for (k in raw) if (raw.hasOwnProperty(k) && raw[k] !== null && raw[k] !== '') out[k] = raw[k];
      } catch (err) {
        console.warn('[frankly] configblok is geen geldige JSON, defaults gebruikt', err);
      }
    }
    if (!Array.isArray(out.tieOrder) || !out.tieOrder.length) out.tieOrder = DEFAULTS.tieOrder;
    if (!out.timeLines || typeof out.timeLines !== 'object') out.timeLines = DEFAULTS.timeLines;
    return out;
  })();

  // ---------- helpers ----------

  // Veilige closest() vanaf een willekeurig event-target.
  function closestFrom(node, selector) {
    var el = node && node.nodeType === 1 ? node : node && node.parentElement;
    return el && el.closest ? el.closest(selector) : null;
  }
  function toArray(list) { return Array.prototype.slice.call(list || []); }
  function show(el) { if (el) el.removeAttribute('hidden'); }
  function hide(el) { if (el) el.setAttribute('hidden', ''); }
  function reduceMotion() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  // localStorage is een beleefdheid, geen slot: alles in try/catch.
  function readStore() {
    try {
      var data = JSON.parse(window.localStorage.getItem(cfg.storageKey));
      if (!data || data.v !== 1 || !data.ts) return null;
      return Date.now() - data.ts > MAX_AGE ? null : data;
    } catch (err) { return null; }
  }
  function writeStore(payload) {
    try { window.localStorage.setItem(cfg.storageKey, JSON.stringify(payload)); } catch (err) {}
  }
  function clearStore() {
    try { window.localStorage.removeItem(cfg.storageKey); } catch (err) {}
  }

  // Routes nooit hardcoden: de winkel heeft een taalkiezer aan.
  function shopRoot() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }
  function cartBase() { return (window.routes && window.routes.cart_url) || shopRoot() + 'cart'; }
  function cartAddUrl() { return (window.routes && window.routes.cart_add_url) || shopRoot() + 'cart/add.js'; }

  // ---------- focus & scroll ----------

  var FOCUSABLE = 'summary, a[href], button:enabled, [tabindex]:not([tabindex^="-"]), [draggable], area, ' +
    'input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe';
  var ownKeydown = null;

  // De modal rendert alle panes tegelijk en verbergt de inactieve met [hidden]
  // (.frankly-scope [hidden] { display: none !important }). Een focuslijst zonder
  // zichtbaarheidsfilter bevat dus de knoppen van álle vragen plus de claimknop:
  // .focus() daarop doet niets en Tab loopt de dialoog uit. Daarom filteren.
  function isReachable(el) {
    if (el.closest('[hidden]')) return false;
    return !!(el.offsetWidth || el.offsetHeight || el.getClientRects().length);
  }

  function focusables(container) {
    return toArray(container.querySelectorAll(FOCUSABLE)).filter(isReachable);
  }

  // Eigen trap: herberekent de lijst bij élke Tab, dus altijd over de actieve pane.
  function ownTrap(container, elementToFocus) {
    ownUntrap();
    ownKeydown = function (event) {
      if (event.key !== 'Tab' && event.keyCode !== 9) return;
      var list = focusables(container);
      var active = document.activeElement;
      if (!list.length) {
        // Niets focusbaars zichtbaar: focus terug naar de dialoog zelf (tabindex=-1).
        event.preventDefault();
        if (active !== container && typeof container.focus === 'function') container.focus();
        return;
      }
      var first = list[0], last = list[list.length - 1];
      if (!container.contains(active)) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
      } else if (event.shiftKey && (active === first || active === container)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', ownKeydown);
    if (elementToFocus) elementToFocus.focus();
  }
  function ownUntrap(elementToFocus) {
    if (ownKeydown) document.removeEventListener('keydown', ownKeydown);
    ownKeydown = null;
    if (elementToFocus) elementToFocus.focus();
  }

  // Bewust NIET window.trapFocus/getFocusableElements van Dawn (assets/global.js):
  // die nemen één momentopname van de eerste en laatste focusbare knop over de hele
  // card, inclusief de verborgen panes. `last` is dan de claimknop uit de
  // resultaatpane, die op vraag 1 display:none is — Tab ontsnapt naar de pagina
  // achter het scrim en Shift+Tab blijft hangen. De handoff noemt de focus trap
  // niet-onderhandelbaar, dus we gebruiken altijd de eigen trap.
  function trap(container, elementToFocus) {
    // Staat er nog een Dawn-trap aan (open drawer), die eerst uitzetten: anders
    // vechten twee keydown-handlers om dezelfde Tab.
    if (typeof window.removeTrapFocus === 'function') window.removeTrapFocus();
    ownTrap(container, elementToFocus);
  }
  function untrap(elementToFocus) {
    ownUntrap(elementToFocus);
  }

  // Scroll-lock: Dawn's .overflow-hidden als die echt iets doet, anders inline.
  var inlineLock = null;
  function lockScroll() {
    document.body.classList.add('overflow-hidden');
    if (window.getComputedStyle(document.body).overflowY !== 'hidden') {
      inlineLock = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
    }
  }
  function unlockScroll() {
    document.body.classList.remove('overflow-hidden');
    if (inlineLock !== null) { document.body.style.overflow = inlineLock; inlineLock = null; }
  }

  // ---------- huidtest-modal ----------

  function initTest(sheet) {
    var card = sheet.querySelector('[data-frankly-card]');
    var body = sheet.querySelector('[data-frankly-body]');
    if (!card || !body) return;

    var stepLabel = sheet.querySelector('[data-frankly-step-label]');
    var progress = sheet.querySelector('[data-frankly-progress]');
    var progressFill = sheet.querySelector('[data-frankly-progress-fill]');
    var timeLineEl = sheet.querySelector('[data-frankly-time-line]');
    var inboxEmail = sheet.querySelector('[data-frankly-inbox-email]');
    var emailInput = sheet.querySelector('[data-frankly-email]');
    var emailError = sheet.querySelector('[data-frankly-email-error]');
    var newsInput = sheet.querySelector('[data-frankly-news]');
    var emailSubmit = sheet.querySelector('[data-frankly-email-submit]');

    var panes = {};
    toArray(sheet.querySelectorAll('[data-frankly-pane]')).forEach(function (pane) {
      panes[pane.getAttribute('data-frankly-pane')] = pane;
    });
    var results = toArray(sheet.querySelectorAll('[data-frankly-result]'));

    // state.news = gaf deze bezoeker marketingtoestemming? Bepaalt of de nieuwsbrief-
    // inschrijving weggaat en, in de Shopify-route, of er überhaupt een mail komt.
    var state = { step: 'q1', scores: { oily: 0, combi: 0, blemish: 0 }, tijd: '', email: '', type: '', handle: '', news: false };
    var openedBy = null, animBusy = false, tOut = null, tIn = null, tClose = null;
    var inputLockUntil = 0, submitted = false;

    // Is de POST die de bevestigingsmail veroorzaakt níet aangekomen? Dan is er geen
    // mail onderweg, hoe goed de configuratie er ook uitziet, en mag het inbox-scherm
    // niet verschijnen. Dat scherm heeft bewust geen knop, dus het zou een doodlopend
    // eind zijn: geen mail, geen claimlink, geen weg terug. canEmail() leest deze
    // vlag; markMailFailed() zet hem. Hij gaat mee in localStorage, zodat een refresh
    // de bezoeker niet alsnog op dat scherm zet.
    var mailFailed = false;
    // Eén keer waarschuwen per paginaweergave; canEmail() wordt vaker aangeroepen.
    var warnedOptIn = false;

    // Antwoordknoppen blijven tijdens de panewissel in de DOM staan (en bij
    // prefers-reduced-motion wisselt de pane meteen), dus een dubbeltik zou
    // dezelfde vraag twee keer scoren of per ongeluk de volgende beantwoorden.
    var INPUT_COOLDOWN = 300;

    // --- weergave ---

    function labelFor(step) {
      if (/^q[1-6]$/.test(step)) return 'Vraag ' + step.charAt(1) + ' van 6';
      if (step === 'email') return 'Bijna klaar';
      if (step === 'result') return 'Je resultaat';
      if (step === 'inbox') return 'Check je inbox';
      return '';
    }

    // Voortgang: q1..q6 = 17..100, e-mail/resultaat/inbox vol.
    function percentFor(step) {
      var m = /^q([1-6])$/.exec(step);
      return m ? Math.round((Number(m[1]) / 6) * 100) : 100;
    }

    // De titel van de zichtbare pane; bij 'result' zit die in het onthulde blok.
    // De test kijkt alleen naar [hidden] BINNEN de pane: bij het hervatten draait
    // applyStep() nog terwijl de sheet zelf hidden is, en dan zou een kale
    // closest('[hidden]') voor élke titel waar zijn en altijd op de eerste (oily)
    // kop terugvallen — de screenreader kondigt dan het verkeerde huidtype aan.
    function visibleTitle(pane) {
      var list = toArray(pane.querySelectorAll('[data-frankly-pane-title]'));
      for (var i = 0; i < list.length; i++) {
        var h = list[i].closest('[hidden]');
        if (!h || h === pane || !pane.contains(h)) return list[i];
      }
      return list[0] || null;
    }

    // aria-labelledby wijst naar één vast id; dat id verhuist mee met de pane.
    function moveTitleId(pane) {
      var target = visibleTitle(pane);
      if (!target) return null;
      var current = document.getElementById(TITLE_ID);
      if (current !== target) {
        if (current) current.removeAttribute('id');
        target.id = TITLE_ID;
      }
      return target;
    }

    // Focus naar de card zelf (role="dialog" tabindex="-1"), zoals Dawn's ModalDialog:
    // de screenreader leest "dialoog" + de titel waar aria-labelledby naar wijst.
    function focusCard() {
      trap(card, card);
    }

    // --- scoren ---

    // Hoogste score wint; gelijkspel lost op in de volgorde van cfg.tieOrder.
    function computeType() {
      var best = cfg.tieOrder[0], bestValue = -1;
      for (var i = 0; i < cfg.tieOrder.length; i++) {
        var value = state.scores[cfg.tieOrder[i]] || 0;
        if (value > bestValue) { bestValue = value; best = cfg.tieOrder[i]; }
      }
      return best;
    }

    function handleFor(key) {
      for (var i = 0; i < results.length; i++) {
        if (results[i].getAttribute('data-frankly-result') === key) {
          return results[i].getAttribute('data-frankly-handle') || '';
        }
      }
      return '';
    }

    // Onthult één server-side gerenderd resultaat, verbergt de andere twee.
    function renderResult() {
      var key = computeType(), chosen = null;
      results.forEach(function (el) {
        if (el.getAttribute('data-frankly-result') === key) { show(el); chosen = el; }
        else hide(el);
      });
      if (!chosen && results.length) {
        chosen = results[0];
        show(chosen);
        key = chosen.getAttribute('data-frankly-result') || key;
      }
      state.type = key;
      state.handle = (chosen && chosen.getAttribute('data-frankly-handle')) || key;
      if (timeLineEl) timeLineEl.textContent = (cfg.timeLines[state.tijd] || cfg.timeLineDefault);
    }

    // --- stapwissel ---

    function persist() {
      var m = /^q([1-6])$/.exec(state.step);
      writeStore({
        v: 1, step: state.step, q: m ? Number(m[1]) : 6, scores: state.scores,
        tijd: state.tijd, email: state.email, type: state.type, handle: state.handle,
        news: state.news, mailFailed: mailFailed, claimed: false, ts: Date.now()
      });
    }

    function applyStep(step, moveFocus) {
      state.step = step;
      Object.keys(panes).forEach(function (name) {
        var pane = panes[name];
        if (name === step) { show(pane); pane.classList.add('is-active'); }
        else { hide(pane); pane.classList.remove('is-active'); }
      });
      if (step === 'result') renderResult();
      if (step === 'inbox' && inboxEmail) inboxEmail.textContent = state.email; // nooit innerHTML

      if (stepLabel) stepLabel.textContent = labelFor(step);
      var pct = percentFor(step);
      if (progressFill) progressFill.style.width = pct + '%';
      if (progress) progress.setAttribute('aria-valuenow', String(pct));

      var pane = panes[step];
      if (pane) {
        moveTitleId(pane);
        if (moveFocus !== false) focusCard();
      }
      persist();
    }

    function setAnim(value) {
      body.classList.remove('is-out', 'is-in');
      if (value) { body.classList.add('is-' + value); body.setAttribute('data-frankly-anim', value); }
      else body.removeAttribute('data-frankly-anim');
    }

    // Transitiehelper uit de handoff: 'out' → 150 ms → patch + 'in' → 30 ms → leeg.
    function goTo(step, moveFocus) {
      if (!panes[step] || step === state.step || animBusy) return;
      if (reduceMotion()) { applyStep(step, moveFocus); return; }
      animBusy = true;
      setAnim('out');
      clearTimeout(tOut); clearTimeout(tIn);
      tOut = setTimeout(function () {
        applyStep(step, moveFocus);
        setAnim('in');
        tIn = setTimeout(function () { setAnim(''); animBusy = false; }, 30);
      }, 150);
    }

    function next() {
      var i = STEPS.indexOf(state.step);
      if (i > -1 && i < STEPS.length - 1) goTo(STEPS[i + 1]);
    }

    // Antwoorden negeren zolang de wissel loopt én kort daarna. De guard zit
    // bewust vóór het scoren: goTo() stapt wel uit op animBusy, maar dan is de
    // score al opgehoogd en komt er een ander huidtype uit.
    function inputLocked() { return animBusy || Date.now() < inputLockUntil; }
    function lockInput() { inputLockUntil = Date.now() + INPUT_COOLDOWN; }

    // --- openen en sluiten ---

    // Het vinkje begint leeg en wordt hier ook leeggemaakt. Dat is niet alleen de
    // begintoestand uit de markup: browsers herstellen bij een refresh vaak zelf de
    // stand van formuliervelden, en dan zou de bezoeker een aangevinkt vakje te zien
    // krijgen dat hij op dít scherm niet heeft aangevinkt. Vooraf aangevinkt is geen
    // geldige toestemming (AVG overweging 32; HvJ Planet49, C-673/17), dus dat vakje
    // is altijd leeg als de e-mailstap opnieuw in beeld komt.
    function clearNewsBox() {
      if (newsInput) newsInput.checked = false;
    }

    function reset() {
      state.scores = { oily: 0, combi: 0, blemish: 0 };
      state.tijd = state.email = state.type = state.handle = '';
      state.news = false;
      mailFailed = false;
      if (emailInput) emailInput.value = '';
      clearNewsBox();
      submitted = false;
      if (emailSubmit) emailSubmit.disabled = false;
      hideEmailError();
    }

    // Hervat een bewaarde staat: jonger dan 7 dagen en nog niet geclaimd.
    function resume() {
      var saved = readStore();
      if (!saved || saved.claimed || STEPS.indexOf(saved.step) === -1) { reset(); return 'q1'; }
      var s = saved.scores || {};
      state.scores = { oily: Number(s.oily) || 0, combi: Number(s.combi) || 0, blemish: Number(s.blemish) || 0 };
      state.tijd = typeof saved.tijd === 'string' ? saved.tijd : '';
      state.email = typeof saved.email === 'string' ? saved.email : '';
      // Wél terughalen wat er destijds is verstuurd (daar hangt canEmail() aan),
      // maar het vakje zelf blijft leeg: dit is een vastlegging van een eerdere
      // handeling, geen alvast ingevulde toestemming voor een volgende.
      state.news = !!saved.news;
      state.type = typeof saved.type === 'string' ? saved.type : '';
      state.handle = typeof saved.handle === 'string' ? saved.handle : '';
      // Een mislukte verzending blijft mislukt: hij wordt niet opnieuw geprobeerd, dus
      // een refresh mag de bezoeker niet alsnog op het inbox-scherm zetten.
      mailFailed = !!saved.mailFailed;
      if (emailInput && state.email) emailInput.value = state.email;
      clearNewsBox();
      hideEmailError();
      // Het inbox-scherm belooft een mail. Kan die er voor deze bezoeker niet zijn —
      // omdat de winkel intussen op een ander ESP is gezet, of omdat de POST die de
      // mail veroorzaakt niet is aangekomen — dan is die belofte niet waar en zetten
      // we hem terug op zijn resultaat, waar de claimknop hem verder helpt.
      if (saved.step === 'inbox' && !canEmail()) return 'result';
      return saved.step;
    }

    function openSheet(opener) {
      clearTimeout(tClose);
      openedBy = opener || document.activeElement;
      applyStep(resume(), false);
      sheet.removeAttribute('hidden');
      lockScroll();
      var reveal = function () { sheet.classList.add('is-open'); focusCard(); };
      if (reduceMotion()) { reveal(); return; }
      // Twee frames wachten, anders slaat de browser de transitie over.
      requestAnimationFrame(function () { requestAnimationFrame(reveal); });
    }

    function closeSheet() {
      if (sheet.hasAttribute('hidden')) return;
      animBusy = false;
      clearTimeout(tOut); clearTimeout(tIn);
      setAnim('');
      sheet.classList.remove('is-open');
      unlockScroll();
      untrap(openedBy && document.contains(openedBy) ? openedBy : null);
      openedBy = null;
      if (reduceMotion()) { hide(sheet); return; }
      tClose = setTimeout(function () { hide(sheet); }, 240);
    }

    // --- e-mailstap ---

    function showEmailError() {
      if (emailError) { emailError.textContent = cfg.emailError; show(emailError); }
      if (emailInput) { emailInput.setAttribute('aria-invalid', 'true'); emailInput.focus(); }
    }
    function hideEmailError() {
      if (emailError) { emailError.textContent = ''; hide(emailError); }
      if (emailInput) emailInput.setAttribute('aria-invalid', 'false');
    }

    function submitEmail() {
      // Eén keer versturen. De knop blijft tijdens de panewissel nog ~180 ms staan
      // en Enter in het veld doet hetzelfde, dus zonder vlag levert een dubbelklik
      // twee inschrijvingen (en twee bevestigingsmails) op.
      if (!emailInput || submitted) return;
      var value = (emailInput.value || '').trim();
      if (!EMAIL_RE.test(value)) { showEmailError(); return; }
      hideEmailError();
      state.email = value;
      var key = computeType();
      var handle = handleFor(key) || key;
      // Toestemming wordt gelezen op het moment van verzenden, uit het vakje zelf.
      state.news = newsInput ? !!newsInput.checked : false;
      submitted = true;
      // Nieuwe verzendpoging, dus schone lei: pas de uitkomst hieronder bepaalt of er
      // een mail onderweg is.
      mailFailed = false;
      if (emailSubmit) emailSubmit.disabled = true;
      // De flow gaat direct door naar het resultaat; verzenden loopt op de
      // achtergrond en mag nooit blokkeren, vertragen of falen zichtbaar maken.
      next();
      setTimeout(function () { sendEmailStep(value, key, handle, state.news); }, 0);
    }

    // --- e-mailverzending, fire-and-forget ---
    //
    // Zie de kop van dit bestand voor de tweedeling transactioneel/marketing. Kort:
    // sendTransactional() loopt altijd (voor zover het kanaal het kan) en levert de
    // bevestigingsmail met de claimlink; sendMarketing() loopt alleen met het vinkje
    // aan en is de nieuwsbriefinschrijving. Ze worden nooit door elkaar gehaald.

    function warn(wat) {
      return function (err) { console.warn('[frankly] ' + wat + ' mislukt', err); };
    }

    // Vastleggen dat de mail-producerende POST niet is aangekomen. Eén plek, zodat
    // canEmail() maar naar één vlag hoeft te kijken. persist() erachteraan: de vlag
    // slaat asynchroon om, ná de stapwissel die de staat wegschreef, en zonder deze
    // regel zou een refresh de oude (te optimistische) staat teruglezen.
    function markMailFailed() {
      mailFailed = true;
      persist();
    }

    // Shopify-route: het verborgen {% form 'customer' %} levert action, form_type en utf8.
    //
    // LET OP: deze POST *is* een nieuwsbriefinschrijving. Shopify maakt er een klant
    // van en zet accepts_marketing van die klant op true; `contact[accepts_marketing]`
    // is geen veld dat dit form_type honoreert, dus het weglaten houdt niets tegen.
    // Daarom staat deze functie onder sendMarketing() en wordt ze uitsluitend
    // aangeroepen als het vinkje aan staat. Een niet-marketing-registratie van het
    // huidtype hoort via een ander kanaal te lopen (app-endpoint of
    // customer-metafield), niet hier — vandaar dat de Shopify-route geen
    // transactionele variant heeft.
    // In deze route is dit óók de enige POST die een mail kan veroorzaken (de
    // opt-in-bevestiging), dus elk faalpad hier zet mailFailed: geen inschrijving,
    // geen bevestigingsmail, dus ook geen inbox-scherm.
    function sendShopify(email, handle) {
      var wrapper = document.querySelector('[data-frankly-customer-form]');
      var form = wrapper ? wrapper.querySelector('form') : null;
      if (!form) {
        markMailFailed();
        console.warn('[frankly] geen [data-frankly-customer-form], niets verstuurd');
        return;
      }
      var data;
      try {
        data = new FormData(form);
      } catch (err) {
        markMailFailed();
        warn('nieuwsbriefinschrijving')(err);
        return;
      }
      data.set('contact[email]', email);
      data.set('contact[tags]', cfg.customerTagPrefix + handle + ',' + cfg.funnelTag);
      fetch(form.action, { method: 'POST', body: data, headers: { Accept: 'text/html' } })
        .then(function (res) {
          // Shopify antwoordt met een redirect naar de pagina zelf; fetch volgt die en
          // levert een 2xx. Een 4xx/5xx betekent dus echt dat er niets is ingeschreven.
          if (!res.ok) throw new Error('Shopify antwoordde met ' + res.status);
        })
        .catch(function (err) {
          markMailFailed();
          warn('nieuwsbriefinschrijving')(err);
        });
    }

    // Beide Klaviyo-aanroepen gaan naar hetzelfde publieke client-endpoint: alleen de
    // company id staat in de browser, nooit een private key. Content-type, Accept en
    // de revisie zijn voor /client/events en /client/subscriptions identiek —
    // application/vnd.api+json is wat de API-specificatie voorschrijft.
    // Geeft de promise terug en verwerpt óók bij een HTTP-fout. fetch verwerpt uit
    // zichzelf alleen bij netwerkfouten, dus een 400 — verkeerde company id, ongeldige
    // metricnaam — zou anders volledig onzichtbaar zijn: geen mail, geen melding, geen
    // spoor. Beide client-endpoints antwoorden bij succes met 202 Accepted. De
    // aanroeper handelt de rejectie af; hier staat bewust geen .catch(), anders is de
    // uitkomst daar niet meer te zien en kan canEmail() er niet op reageren.
    function klaviyoPost(path, payload) {
      return fetch('https://a.klaviyo.com/client/' + path + '?company_id=' + encodeURIComponent(cfg.klaviyoCompanyId), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/vnd.api+json',
          Accept: 'application/vnd.api+json',
          revision: KLAVIYO_REVISION
        },
        body: JSON.stringify(payload)
      }).then(function (res) {
        if (!res.ok) throw new Error('Klaviyo antwoordde met ' + res.status);
        return res;
      });
    }

    // TRANSACTIONEEL — Klaviyo client events. Dit event maakt of werkt het profiel bij
    // ZONDER het op een lijst te zetten, en is de trigger onder de flow die de
    // bevestigingsmail met de claimlink stuurt. Geen marketing, dus geen vinkje nodig.
    function sendKlaviyoEvent(email, key, handle) {
      var payload = {
        data: {
          type: 'event',
          attributes: {
            // Het huidtype hangt aan het EVENT, niet aan het profiel: de flow-mail
            // bouwt de claimlink met {{ event.huidtype }} (§3e stap 7 van
            // FRANKLY-SAMPLING-FUNNEL.md). Zet het hier niet óók als profieleigenschap.
            // Bij wie het vinkje aanzette doet sendKlaviyoSubscription() dat al, en bij
            // wie dat niet deed hoort er zonder toestemming zo min mogelijk op het
            // profiel terecht te komen — dat profiel bestaat alleen om deze ene
            // transactionele mail te kunnen sturen.
            properties: { huidtype: handle, huidtype_key: key },
            metric: { data: { type: 'metric', attributes: { name: cfg.klaviyoMetric } } },
            profile: { data: { type: 'profile', attributes: { email: email } } }
          }
        }
      };
      // Komt deze POST niet aan, dan komt de bevestigingsmail er ook niet. Dat is de
      // enige plek waar dat te merken valt, dus leggen we het hier vast: het
      // inbox-scherm mag daarna niet meer verschijnen.
      klaviyoPost('events', payload).catch(function (err) {
        markMailFailed();
        warn('transactionele mail')(err);
      });
    }

    // MARKETING — Klaviyo client subscriptions. Zet de toestemming op SUBSCRIBED en
    // hangt het profiel aan de lijst. Alleen met het vinkje aan.
    function sendKlaviyoSubscription(email, key, handle) {
      var payload = {
        data: {
          type: 'subscription',
          attributes: {
            custom_source: 'Frankly huidtest',
            profile: {
              data: {
                type: 'profile',
                attributes: {
                  email: email,
                  properties: { huidtype: handle, huidtype_key: key, funnel: cfg.funnelTag },
                  subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } }
                }
              }
            }
          },
          relationships: { list: { data: { type: 'list', id: cfg.klaviyoListId } } }
        }
      };
      // Mislukt deze POST, dan is er geen inschrijving — maar de bevestigingsmail
      // hangt er niet aan (die loopt via het event), dus mailFailed blijft ongemoeid.
      klaviyoPost('subscriptions', payload).catch(warn('nieuwsbriefinschrijving'));
    }

    // De bevestigingsmail met de claimlink. Loopt ALTIJD, ongeacht het vinkje: dit is
    // de afhandeling van een verzoek dat de bezoeker zelf deed. Alleen de
    // Klaviyo-route kan dit — Shopify kent geen manier om te mailen zonder in te
    // schrijven, en bij esp 'none' gaat er per definitie niets weg.
    function sendTransactional(email, key, handle) {
      if (cfg.esp !== 'klaviyo') return;
      // Een half ingerichte Klaviyo-route ziet er van buiten gezond uit: de funnel
      // loopt door, alleen komt er nooit een mail aan. Daarom hier wél een melding.
      if (!cfg.klaviyoCompanyId) {
        console.warn('[frankly] klaviyoCompanyId ontbreekt, geen bevestigingsmail verstuurd');
        return;
      }
      sendKlaviyoEvent(email, key, handle);
    }

    // De nieuwsbrief. Uitsluitend aanroepen met toestemming; deze functie controleert
    // dat bewust niet zelf, zodat er precies één plek is waar het vinkje wordt
    // uitgelezen en die niet stilzwijgend ergens anders wordt overruled.
    function sendMarketing(email, key, handle) {
      if (cfg.esp === 'klaviyo') {
        if (cfg.klaviyoCompanyId && cfg.klaviyoListId) sendKlaviyoSubscription(email, key, handle);
        // Hier heeft iemand actief toestemming gegeven en rekent hij op de korting uit
        // de labeltekst. Gaat dat door een ontbrekende id niet door, dan mag dat niet
        // stilzwijgend gebeuren: de funnel oogt gezond en alleen de lijst blijft leeg.
        else console.warn('[frankly] klaviyoCompanyId of klaviyoListId ontbreekt, nieuwsbriefinschrijving overgeslagen');
        return;
      }
      if (cfg.esp === 'shopify') sendShopify(email, handle);
    }

    // De e-mailstap in één aanroep: eerst de bevestigingsmail, daarna — en alleen met
    // toestemming — de nieuwsbrief. De sample hangt van geen van beide af: de flow
    // loopt hoe dan ook door naar het resultaat.
    //
    // Elk van de twee heeft zijn eigen try/catch. Struikelt de ene, dan gaat de
    // andere gewoon door: een kapotte bevestigingsmail mag geen gegeven toestemming
    // laten verdampen, en een kapotte inschrijving mag de sample niet tegenhouden.
    function sendEmailStep(email, key, handle, news) {
      try {
        sendTransactional(email, key, handle);
      } catch (err) {
        // Struikelt de aanroep zelf, dan is er net zo goed niets verstuurd.
        if (cfg.esp === 'klaviyo') markMailFailed();
        warn('transactionele mail')(err);
      }
      if (!news) return;
      try {
        sendMarketing(email, key, handle);
      } catch (err) {
        // In de Shopify-route is de inschrijving óók de enige mail; in de
        // Klaviyo-route staan ze los en blijft de bevestigingsmail overeind.
        if (cfg.esp === 'shopify') markMailFailed();
        warn('nieuwsbriefinschrijving')(err);
      }
    }

    // Kan er voor deze bezoeker echt een mail uitgaan? Eén bron van waarheid, twee
    // gebruikers: welke pane de claimknop opent, en of het inbox-scherm klopt.
    function canEmail() {
      // Is de POST die de mail veroorzaakt afgeketst, dan helpt geen enkele
      // instelling meer: er is niets verstuurd, dus er komt niets aan.
      if (mailFailed) return false;
      // Klaviyo mailt transactioneel, dus ook zonder vinkje; de list id is alleen
      // voor de nieuwsbrief nodig.
      if (cfg.esp === 'klaviyo') return !!cfg.klaviyoCompanyId;
      // Shopify mailt alleen via de inschrijving, en die vraagt om toestemming. En
      // zelfs mét vinkje komt er alleen post als dubbele opt-in aan staat: die
      // bevestigingsmail ís de enige mail die deze route oplevert. Staat de instelling
      // uit, dan wordt de klant stilzwijgend ingeschreven en gaat er niets uit. Het
      // theme kan die admin-instelling niet uitlezen, dus de winkelier bevestigt hem;
      // zonder die bevestiging beloven we geen mail.
      if (cfg.esp === 'shopify') {
        if (state.news && !cfg.shopifyDoubleOptIn && !warnedOptIn) {
          warnedOptIn = true;
          console.warn('[frankly] shopifyDoubleOptIn staat uit: Shopify stuurt dan geen bevestigingsmail, dus het inbox-scherm wordt overgeslagen');
        }
        return !!state.news && !!cfg.shopifyDoubleOptIn;
      }
      return false;
    }

    // --- claimen vanaf de resultaatpane ---

    // Komt er een mail aan, dan is "kijk in je mail" de volgende stap en wacht de
    // bezoeker op de claimlink. Komt die mail er niet — Shopify zonder vinkje of
    // zonder dubbele opt-in, helemaal geen ESP, of een verzending die is afgeketst —
    // dan zou datzelfde scherm hem laten wachten op post die nooit komt, zonder knop
    // om verder te gaan. In dat geval brengen we hem meteen naar de claimpagina.
    function goToClaim() {
      if (canEmail()) { goTo('inbox'); return; }
      var handle = state.handle || handleFor(computeType()) || computeType();
      state.handle = handle;
      // Eerst wegschrijven, dán navigeren: de claimpagina leest ?type=, maar valt op
      // deze staat terug. Draai je de volgorde om, dan is de pagina al geladen
      // voordat het huidtype is bewaard.
      persist();
      var base = cfg.claimUrl || DEFAULTS.claimUrl;
      window.location.href = base + (base.indexOf('?') === -1 ? '?' : '&') +
        'type=' + encodeURIComponent(handle);
    }

    // --- events ---

    // Delegatie op document, zodat ook later ingevoegde openknoppen werken.
    document.addEventListener('click', function (event) {
      var opener = closestFrom(event.target, '[data-frankly-open-test]');
      if (!opener) return;
      event.preventDefault();
      openSheet(opener);
    });

    sheet.addEventListener('click', function (event) {
      if (closestFrom(event.target, '[data-frankly-close]')) { event.preventDefault(); closeSheet(); return; }
      if (closestFrom(event.target, '[data-frankly-scrim]')) { closeSheet(); return; }

      // Antwoord op q1..q5: scoort en gaat direct door.
      var scored = closestFrom(event.target, '[data-frankly-score]');
      if (scored) {
        event.preventDefault();
        if (inputLocked()) return;
        var key = scored.getAttribute('data-frankly-score');
        if (state.scores.hasOwnProperty(key)) state.scores[key] += 1;
        lockInput();
        next();
        return;
      }
      // Vraag 6: zet alleen de tijdvoorkeur, scoort niet.
      var timed = closestFrom(event.target, '[data-frankly-time]');
      if (timed) {
        event.preventDefault();
        if (inputLocked()) return;
        state.tijd = timed.getAttribute('data-frankly-time') || '';
        lockInput();
        next();
        return;
      }
      if (closestFrom(event.target, '[data-frankly-email-submit]')) { event.preventDefault(); submitEmail(); return; }
      if (closestFrom(event.target, '[data-frankly-claim]')) { event.preventDefault(); goToClaim(); }
    });

    if (emailInput) {
      emailInput.addEventListener('input', hideEmailError);
      emailInput.addEventListener('keydown', function (event) {
        if (event.key !== 'Enter') return;
        event.preventDefault();
        submitEmail();
      });
    }

    document.addEventListener('keydown', function (event) {
      if (event.key !== 'Escape' && event.key !== 'Esc') return;
      if (!sheet.hasAttribute('hidden')) closeSheet();
    });
  }

  // ---------- claimpagina ----------

  function initClaim(root) {
    var panes = toArray(root.querySelectorAll('[data-frankly-claim-pane]'));
    var fallbackPane = root.querySelector('[data-frankly-claim-fallback]');
    var claimedPane = root.querySelector('[data-frankly-claim-claimed]');
    var warning = root.querySelector('[data-frankly-cart-warning]');

    // De sectie rendert de "al geclaimd"-pane server-side zichtbaar: dan niets doen.
    if (claimedPane && !claimedPane.hasAttribute('hidden')) return;

    var confirmPending = false, busy = false, labelBefore = null;

    // De waarschuwing is de enige terugkoppeling vóór we een winkelwagen legen, dus
    // moet ze ook aangekondigd worden. role="alert" hoort in de markup; staat het er
    // niet, dan zetten we het hier alsnog. Idem het id, voor aria-describedby.
    if (warning) {
      if (!warning.getAttribute('role')) warning.setAttribute('role', 'alert');
      if (!warning.id) warning.id = 'frankly-cart-warning';
    }

    function showWarning(text) {
      if (!warning) { console.warn('[frankly] geen [data-frankly-cart-warning]:', text); return; }
      // Eerst zichtbaar maken, dan de tekst zetten: een live region die al in de
      // boom staat wordt wél voorgelezen, tekst in een hidden element niet.
      show(warning);
      warning.textContent = text; // nooit innerHTML
    }
    function hideWarning() {
      if (warning) { warning.textContent = ''; hide(warning); }
    }

    // "cart leegmaken" is een sectie-instelling; ontbreekt hij, dan legen we niets.
    function clearEnabled() {
      var value = root.getAttribute('data-frankly-clear-cart');
      return value === 'true' || value === '1';
    }

    function paneFor(value) {
      if (!value) return null;
      for (var i = 0; i < panes.length; i++) {
        if (panes[i].getAttribute('data-frankly-handle') === value ||
            panes[i].getAttribute('data-frankly-claim-pane') === value) return panes[i];
      }
      return null;
    }

    // Pane kiezen: ?type= wint, dan het opgeslagen type, dan de fallback.
    var wanted = '';
    try { wanted = (new URLSearchParams(window.location.search).get('type') || '').trim(); } catch (err) {}
    var saved = readStore();
    var chosen = paneFor(wanted) ||
      (saved ? paneFor(saved.handle) || paneFor(saved.type) : null) ||
      fallbackPane || panes[0] || null;
    if (chosen) {
      panes.forEach(hide);
      hide(fallbackPane);
      hide(claimedPane);
      show(chosen);
    }

    var activeButton = chosen ? chosen.querySelector('[data-frankly-add-sample]') : null;

    // De waarschuwing staat in de markup onderaan .frankly-claim, dus ná het
    // "in je pakketje"-paneel: op mobiel honderden pixels onder de knop, buiten
    // beeld. Bij de knop hangen, waar de tekst hoort. Zelfde klasse, zelfde sectie,
    // dus de styling verandert niet.
    if (warning && activeButton && activeButton.parentNode &&
        !activeButton.parentNode.contains(warning)) {
      try {
        activeButton.parentNode.insertBefore(warning, activeButton.nextSibling);
      } catch (err) { /* blijft staan waar hij stond */ }
    }

    // Verwijst de knop al naar een zichtbare uitleg in de markup — iets anders dan
    // deze waarschuwing zelf — dan die laten staan.
    function hasStaticNote(button) {
      var ids = (button.getAttribute('aria-describedby') || '').split(/\s+/);
      for (var i = 0; i < ids.length; i++) {
        if (!ids[i] || (warning && ids[i] === warning.id)) continue;
        var el = document.getElementById(ids[i]);
        if (el && !el.hasAttribute('hidden')) return true;
      }
      return false;
    }

    // Zonder variant-ID is de knop niet bruikbaar: uitzetten met zichtbare uitleg,
    // maar niet dezelfde tekst twee keer onder elkaar.
    if (activeButton && !(activeButton.getAttribute('data-variant-id') || '').trim()) {
      activeButton.disabled = true;
      if (!hasStaticNote(activeButton)) showWarning(TXT_NO_VARIANT);
    }

    // --- cart ---

    function cartGet() {
      return fetch(cartBase() + '.js', { method: 'GET', headers: { Accept: 'application/json' } })
        .then(function (res) { return res.json(); })
        // Onbekende cart als leeg behandelen, zodat we nooit onnodig legen.
        .catch(function () { return { items: [], item_count: 0 }; });
    }

    // fetch verwerpt alleen bij netwerkfouten, dus res.ok zelf controleren: anders
    // gaat de flow na een mislukte leging vrolijk door en belandt de bezoeker met
    // zijn betaalde artikelen én het sample in de checkout.
    function cartClear() {
      return fetch(cartBase() + '/clear.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' }
      }).then(function (res) {
        if (!res.ok) throw new Error(TXT_CLEAR_FAILED);
      });
    }

    function cartAdd(variantId) {
      var n = Number(variantId);
      var id = isFinite(n) && n > 0 && String(n) === variantId ? n : variantId;
      return fetch(cartAddUrl(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: id, quantity: 1 }] })
      }).then(function (res) {
        return res.json().catch(function () { return {}; }).then(function (data) {
          if (res.ok) return data;
          // Fouten uit de validation function komen niet betrouwbaar door de
          // AJAX-API heen, dus altijd een eigen NL-tekst achter de hand.
          throw new Error(data.description || data.message || TXT_FAILED);
        });
      });
    }

    // Alles in de cart dat niet het sample zelf is.
    function foreignCount(cart, variantId) {
      var items = (cart && cart.items) || [], total = 0;
      for (var i = 0; i < items.length; i++) {
        var id = String(items[i].variant_id != null ? items[i].variant_id : items[i].id);
        if (id !== String(variantId)) total += items[i].quantity || 1;
      }
      return total;
    }

    function setBusy(button, on) {
      busy = on;
      button.disabled = on;
      button.setAttribute('aria-busy', on ? 'true' : 'false');
    }

    // De waarschuwing aan de knop koppelen zonder te overschrijven: de markup zet
    // er zelf al een id in (de uitleg bij een uitgeschakelde knop, en in de sectie
    // ook al deze waarschuwing). Alleen toevoegen, nooit weghalen.
    function describeByWarning(button) {
      if (!warning || !warning.id) return;
      var list = (button.getAttribute('aria-describedby') || '').split(/\s+/);
      var out = [], seen = false, i;
      for (i = 0; i < list.length; i++) {
        if (!list[i]) continue;
        if (list[i] === warning.id) seen = true;
        out.push(list[i]);
      }
      if (seen) return;
      out.push(warning.id);
      button.setAttribute('aria-describedby', out.join(' '));
    }

    // De tweede klik leegt de winkelwagen, dus die klik moet een bewuste
    // bevestiging zijn: de knop zegt zelf wat er gaat gebeuren, en de waarschuwing
    // wordt via role="alert" voorgelezen.
    function enterConfirm(button) {
      if (labelBefore === null) labelBefore = button.textContent;
      button.textContent = TXT_CONFIRM;
      describeByWarning(button);
    }
    function leaveConfirm(button) {
      confirmPending = false;
      if (labelBefore !== null) { button.textContent = labelBefore; labelBefore = null; }
    }

    function handleClaim(button) {
      if (busy) return;
      var variantId = (button.getAttribute('data-variant-id') || '').trim();
      if (!variantId) { button.disabled = true; showWarning(TXT_NO_VARIANT); return; }
      setBusy(button, true);
      hideWarning();

      cartGet().then(function (cart) {
        var mustClear = foreignCount(cart, variantId) > 0 && clearEnabled();
        // Eerste klik met een gevulde cart: waarschuwen en wachten op de tweede.
        // De cart wordt nooit stilzwijgend geleegd.
        if (mustClear && !confirmPending) {
          confirmPending = true;
          showWarning(TXT_CLEAR);
          enterConfirm(button);
          setBusy(button, false);
          return null;
        }
        var chain = Promise.resolve();
        if (mustClear) chain = chain.then(cartClear);
        return chain.then(function () { return cartAdd(variantId); });
      }).then(function (result) {
        if (!result) return; // wacht op de tweede klik
        clearStore();
        // Vlag achterlaten zodat de modal de test niet hervat na een claim.
        writeStore({ v: 1, step: 'claimed', claimed: true, ts: Date.now() });
        window.location.href = shopRoot() + 'checkout';
      }).catch(function (err) {
        setBusy(button, false);
        // Mislukt er iets, dan begint de bevestiging opnieuw: is de cart nog
        // gevuld, dan volgt eerst weer de waarschuwing. Nooit stilzwijgend legen.
        leaveConfirm(button);
        showWarning((err && err.message) || TXT_FAILED);
      });
    }

    root.addEventListener('click', function (event) {
      var button = closestFrom(event.target, '[data-frankly-add-sample]');
      if (!button) return;
      event.preventDefault();
      handleClaim(button);
    });
  }

  // ---------- start; beide onderdelen zijn optioneel ----------

  function boot() {
    var sheet = document.querySelector('[data-frankly-sheet]');
    if (sheet) initTest(sheet);
    var claimRoot = document.querySelector('[data-frankly-claim-root]');
    if (claimRoot) initClaim(claimRoot);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
